import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Settings,
  LogOut,
  Sparkles,
  ChevronRight,
  Clock,
  FileText,
  HelpCircle,
  Link as LinkIcon,
} from "lucide-react";

const navItems = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/submit-link", label: "Submit Link", icon: LinkIcon },
  { path: "/admin/products", label: "Products", icon: Package },
  { path: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { path: "/admin/payments", label: "Pending Payments", icon: Clock },
  { path: "/admin/submissions", label: "Submissions", icon: FileText },
  { path: "/admin/help-requests", label: "Help Requests", icon: HelpCircle },
  { path: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar. Pinned below the 40px ticker: `fixed h-full` started at the
          ticker offset but was still a full viewport tall, so the bottom 40px —
          the sign-out row — sat below the fold. */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col fixed top-10 bottom-0 overflow-y-auto">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-orange-500" />
            <div>
              <span className="font-bold text-lg">CheepSweepz</span>
              <span className="text-orange-400 text-xs ml-1">CONCIERGE</span>
            </div>
          </Link>
          <p className="text-xs text-slate-500 mt-1">Admin Panel</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                      isActive
                        ? "bg-orange-600 text-white"
                        : "text-slate-400 hover:text-white hover:bg-slate-800"
                    }`}
                    data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                    {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
              <span className="text-sm font-medium">
                {user?.name?.charAt(0) || "A"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="flex-1 text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="admin-view-store"
            >
              View Store
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-400 hover:text-red-400 hover:bg-slate-800"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64">
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
